/**
 * ARKS Store - Favourites & Wishlist Master Engine
 */

class ARKSWishlist {
  constructor() {
    this.storageKey = 'arks_store_wishlist_items';
    this.fallbackKey = 'arks_wishlist';
    this.init();
  }

  init() {
    this.syncStorageKeys();
    this.updateBadges();
    this.bindEvents();
    this.syncActiveButtons();
    this.renderWishlistPage();
  }

  syncStorageKeys() {
    try {
      const primary = localStorage.getItem(this.storageKey);
      const fallback = localStorage.getItem(this.fallbackKey);
      if (!primary && fallback) {
        localStorage.setItem(this.storageKey, fallback);
      } else if (primary && !fallback) {
        localStorage.setItem(this.fallbackKey, primary);
      }
    } catch (e) {}
  }

  getItems() {
    try {
      const items = localStorage.getItem(this.storageKey) || localStorage.getItem(this.fallbackKey);
      return items ? JSON.parse(items) : [];
    } catch (e) {
      console.error('Wishlist storage access error:', e);
      return [];
    }
  }

  saveItems(items) {
    try {
      const json = JSON.stringify(items);
      localStorage.setItem(this.storageKey, json);
      localStorage.setItem(this.fallbackKey, json);
      this.updateBadges();
      this.syncActiveButtons();
      this.renderWishlistPage();
    } catch (e) {
      console.error('Wishlist save error:', e);
    }
  }

  updateBadges() {
    const items = this.getItems();
    const count = items.length;

    // Header, drawer, and profile badges
    document.querySelectorAll('.wishlist-count-badge, .wishlist-pill-count, .profile-wishlist-badge').forEach(badge => {
      badge.textContent = count;
      if (count > 0) {
        badge.style.display = badge.tagName === 'SPAN' && badge.classList.contains('profile-wishlist-badge') ? 'inline-block' : 'flex';
      } else {
        if (badge.classList.contains('profile-wishlist-badge')) {
          badge.textContent = '0';
          badge.style.display = 'inline-block';
        } else {
          badge.style.display = 'none';
        }
      }
    });

    const pill = document.getElementById('WishlistTotalCountPill');
    if (pill) {
      pill.textContent = `${count} ${count === 1 ? 'Item' : 'Items'}`;
    }
  }

  isWishlisted(handle) {
    if (!handle) return false;
    const items = this.getItems();
    return items.some(item => item.handle === handle);
  }

  toggleItem(productData, triggerEl = null) {
    if (!productData || !productData.handle) return;

    let items = this.getItems();
    const index = items.findIndex(item => item.handle === productData.handle);
    let isAdded = false;

    if (index > -1) {
      items.splice(index, 1);
      isAdded = false;
    } else {
      items.unshift(productData);
      isAdded = true;
    }

    this.saveItems(items);

    if (triggerEl) {
      triggerEl.classList.toggle('is-active', isAdded);
      // Pop animation
      triggerEl.style.transform = 'scale(1.35)';
      setTimeout(() => { triggerEl.style.transform = ''; }, 200);
    }

    if (typeof showArksToast === 'function') {
      if (isAdded) {
        showArksToast(`❤️ Added to Wishlist! <a href="/pages/wishlist" style="color:#fff;text-decoration:underline;font-weight:700;margin-left:8px;">View Wishlist &rarr;</a>`);
      } else {
        showArksToast('Removed from Wishlist');
      }
    }
  }

  removeItem(handle) {
    if (!handle) return;
    let items = this.getItems().filter(item => item.handle !== handle);
    this.saveItems(items);
    if (typeof showArksToast === 'function') {
      showArksToast('Item removed from wishlist');
    }
  }

  clearAll() {
    this.saveItems([]);
    if (typeof showArksToast === 'function') {
      showArksToast('Wishlist cleared');
    }
  }

  syncActiveButtons() {
    const items = this.getItems();
    const handles = items.map(i => i.handle);

    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const handle = btn.dataset.productHandle;
      if (handles.includes(handle)) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-label', 'Remove from Wishlist');
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-label', 'Add to Wishlist');
      }
    });

    // Also sync PDP main wishlist button
    const pdpBtn = document.querySelector('.pdp-btn-wishlist');
    if (pdpBtn && pdpBtn.dataset.productHandle) {
      const isFav = handles.includes(pdpBtn.dataset.productHandle);
      pdpBtn.classList.toggle('is-active', isFav);
      const labelSpan = pdpBtn.querySelector('.pdp-wishlist-label') || pdpBtn.querySelectorAll('span')[1];
      const iconSpan = pdpBtn.querySelector('.pdp-wishlist-icon') || pdpBtn.querySelector('.pdp-btn-icon');
      if (labelSpan) labelSpan.textContent = isFav ? 'WISHLISTED' : 'WISHLIST';
      if (iconSpan) iconSpan.textContent = isFav ? '❤️' : '♡';
    }

    // Sync sticky bar wishlist icon
    const stickyWishBtn = document.querySelector('.pdp-sticky-wishlist-btn');
    if (stickyWishBtn) {
      const mainWish = document.querySelector('.pdp-btn-wishlist');
      const isFav = mainWish && mainWish.dataset.productHandle ? handles.includes(mainWish.dataset.productHandle) : false;
      stickyWishBtn.classList.toggle('is-active', isFav);
      const stickyIcon = stickyWishBtn.querySelector('.pdp-sticky-wishlist-icon');
      if (stickyIcon) stickyIcon.textContent = isFav ? '❤️' : '♡';
    }
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      // Wishlist toggle button click on any card or PDP
      const toggleBtn = e.target.closest('[data-wishlist-toggle], .pdp-btn-wishlist');
      if (toggleBtn) {
        e.preventDefault();
        e.stopPropagation();

        const productData = {
          handle: toggleBtn.dataset.productHandle || 'smart-gadget-essential',
          title: toggleBtn.dataset.productTitle || 'ARKS Problem Solver Essential',
          price: toggleBtn.dataset.productPrice || '₹499.00',
          comparePrice: toggleBtn.dataset.productComparePrice || '₹999.00',
          image: toggleBtn.dataset.productImage || 'https://images.unsplash.com/photo-1583863788434-e58a36330cf0?w=600&auto=format&fit=crop&q=80',
          url: toggleBtn.dataset.productUrl || `/products/${toggleBtn.dataset.productHandle || 'smart-gadget-essential'}`,
          variantId: toggleBtn.dataset.productVariantId || ''
        };

        this.toggleItem(productData, toggleBtn);
      }
    });
  }

  renderWishlistPage() {
    const containers = document.querySelectorAll('#WishlistGridContainer');
    if (!containers || containers.length === 0) return;

    const items = this.getItems();
    const emptyStates = document.querySelectorAll('#WishlistEmptyState');
    const headerActions = document.querySelectorAll('#WishlistHeaderActions');
    const infoBanners = document.querySelectorAll('#WishlistInfoBanner');

    if (items.length === 0) {
      containers.forEach(c => {
        c.innerHTML = '';
        c.style.display = 'none';
      });
      emptyStates.forEach(s => s.style.display = 'block');
      headerActions.forEach(a => a.style.display = 'none');
      infoBanners.forEach(b => b.style.display = 'none');
      return;
    }

    emptyStates.forEach(s => s.style.display = 'none');
    headerActions.forEach(a => a.style.display = 'flex');
    infoBanners.forEach(b => b.style.display = 'block');

    let html = `
      <div class="wishlist-products-grid" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 24px;">
    `;

    items.forEach(item => {
      const displayPrice = item.price || '₹499.00';
      const displayCompare = item.comparePrice || '₹999.00';
      const displayImg = item.image || 'https://images.unsplash.com/photo-1583863788434-e58a36330cf0?w=600&auto=format&fit=crop&q=80';
      const itemUrl = item.url || `/products/${item.handle}`;

      html += `
        <div class="product-card wishlist-card-item" data-product-handle="${item.handle}">
          <div class="product-card-media">
            <a href="${itemUrl}">
              <img src="${displayImg}" alt="${item.title}" class="product-card-image" loading="lazy">
            </a>

            <!-- Active Heart Button to Remove -->
            <button type="button" class="wishlist-toggle-btn is-active" 
              data-wishlist-toggle 
              data-product-handle="${item.handle}"
              data-product-title="${item.title.replace(/"/g, '&quot;')}"
              data-product-price="${displayPrice}"
              data-product-compare-price="${displayCompare}"
              data-product-image="${displayImg}"
              data-product-url="${itemUrl}"
              data-product-variant-id="${item.variantId || ''}"
              title="Remove from Wishlist"
              aria-label="Remove from Wishlist">
              <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
            </button>

            <!-- Save Discount Badge -->
            <div class="product-badge-overlay badge-discount">50% OFF</div>
          </div>

          <div class="product-card-content">
            <div class="product-card-vendor">ARKS Essentials</div>
            <h3 class="product-card-title">
              <a href="${itemUrl}">${item.title}</a>
            </h3>

            <div class="product-card-rating" style="display: flex; align-items: center; gap: 4px; font-size: 13px; color: #f59e0b; margin-bottom: 6px;">
              <span>★★★★★</span>
              <span style="color: #6b7280; font-weight: 600; font-size: 12px;">4.9 (120+)</span>
            </div>

            <div class="product-card-price-row">
              <span class="price-current">${displayPrice}</span>
              ${displayCompare ? `<span class="price-compare">${displayCompare}</span>` : ''}
              <span class="price-discount-pill">SAVE 50%</span>
            </div>

            <div class="product-card-actions" style="margin-top: 12px; display: flex; flex-direction: column; gap: 8px;">
              <button type="button" class="btn btn-primary btn-full" onclick="wishlistMoveToBag('${item.handle}', '${item.title.replace(/'/g, "\\'")}', '${displayPrice}', '${displayImg}', '${item.variantId || ''}')" style="border-radius: 8px; font-weight: 700; padding: 11px 16px;">
                MOVE TO BAG 🛍️
              </button>
            </div>
          </div>
        </div>
      `;
    });

    html += `</div>`;

    containers.forEach(c => {
      c.innerHTML = html;
      c.style.display = 'block';
    });
  }
}

// Global Move to Bag Helper
window.wishlistMoveToBag = function(handle, title, price, image, variantId) {
  if (window.ARKS_QuickCart && typeof window.ARKS_QuickCart.add === 'function') {
    window.ARKS_QuickCart.add(handle, title, price, image);
  } else if (variantId) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
    .then(r => r.json())
    .then(() => {
      if (window.ARKS_CartDrawer) window.ARKS_CartDrawer.open();
      if (typeof showArksToast === 'function') showArksToast('Added to bag!');
    })
    .catch(() => {
      if (window.ARKS_QuickCart) window.ARKS_QuickCart.add(handle, title, price, image);
    });
  } else {
    // Fallback drawer open
    if (window.ARKS_CartDrawer) window.ARKS_CartDrawer.open();
  }

  if (typeof showArksToast === 'function') {
    showArksToast(`🛍️ ${title} moved to Bag!`);
  }
};

// Global Clear Wishlist Helper
window.clearAllWishlistItems = function() {
  if (confirm('Are you sure you want to clear all items from your wishlist?')) {
    if (window.ARKS_Wishlist) {
      window.ARKS_Wishlist.clearAll();
    }
  }
};

// Initialize on DOM Ready
document.addEventListener('DOMContentLoaded', () => {
  window.ARKS_Wishlist = new ARKSWishlist();
});

// Also initialize on turbo / dynamic navigation
window.addEventListener('load', () => {
  if (window.ARKS_Wishlist) {
    window.ARKS_Wishlist.updateBadges();
    window.ARKS_Wishlist.syncActiveButtons();
    window.ARKS_Wishlist.renderWishlistPage();
  }
});
